import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { getLanguageValue } from "locales/i18n";
import { PAGE_TYPE } from "enums";
import { getImageUrl } from "helpers/Image.helper";
import { formatTimeAgo } from "helpers/Datetime.helper";
import { updateCommentLike, removeCommentById, addComment, findCommentById } from "helpers/Comment.helper";
import { IDiaryComment } from "api/interfaces/profile.interface";
import { getDiaryComments, createDiaryComment, deleteDiaryComment, toggleCommentLike } from "api/student/v2/diary.api";
import { getCurrentLangName } from "helpers/String.helper";
import { usePopup } from "contexts/PopupContext";
import KoachInfoPopup from "../../popup/KoachInfoPopup";
import StudentInfoPopup from "../../popup/StudentInfoPopup";
import CommentItem from "../../../../common/v2/CommentItem";
import ReportPopup from "commons/popup/ReportPopup";

interface CommentSectionProps {
    diaryId: string;
    diaryAuthorId: string;
}

const CommentSection: React.FC<CommentSectionProps> = ({ diaryId, diaryAuthorId }) => {
    const { info: userInfo } = useSelector((state: any) => state.user);
    const { showPopup } = usePopup();

    // 댓글 관련 상태
    const [commentText, setCommentText] = useState("");
    const [comments, setComments] = useState<IDiaryComment[]>([]);
    const [commentsLoading, setCommentsLoading] = useState(false);
    const [commentLikeLoading, setCommentLikeLoading] = useState<{ [key: number]: boolean }>({});
    const [commentSubmitting, setCommentSubmitting] = useState(false);

    // 케밥 메뉴 관련 상태
    const [activeKebabMenu, setActiveKebabMenu] = useState<number | null>(null);
    const kebabRef = useRef<HTMLDivElement>(null);

    // 댓글 무한 스크롤을 위한 상태
    const [commentCurrentPage, setCommentCurrentPage] = useState(1);
    const [commentHasMore, setCommentHasMore] = useState(true);
    const [commentLoadingMore, setCommentLoadingMore] = useState(false);
    const [commentsPagination, setCommentsPagination] = useState({ currentPage: 1, hasNextPage: false });

    // 대댓글 관련 상태
    const [replyToComment, setReplyToComment] = useState<number | null>(null);
    const [replyText, setReplyText] = useState("");

    // 신고 관련 상태
    const [showReportPopup, setShowReportPopup] = useState(false);
    const [commentToReport, setCommentToReport] = useState<{ id: number; content: string } | null>(null);

    useEffect(() => {
        if (diaryId) {
            fetchCommentsData(1, false);
        }
    }, [diaryId]);

    // 통일된 사용자 클릭 핸들러
    const handleUserClick = (userId: string, userType: string) => {
        if (userType === 'student') {
        const popupContent = (
                <StudentInfoPopup
                    studentId={userId}
                onClose={() => showPopup(null)}
            />
        );
        showPopup(popupContent);
        } else if (userType === 'koach') {
        const popupContent = (
                <KoachInfoPopup
                    koachId={userId}
                onClose={() => showPopup(null)}
            />
        );
        showPopup(popupContent);
        }
    };

    // 케밥 메뉴 토글
    const toggleKebabMenu = (commentId: number) => {
        setActiveKebabMenu(activeKebabMenu === commentId ? null : commentId);
    };

    // 케밥 메뉴 외부 클릭 감지
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (kebabRef.current && !kebabRef.current.contains(event.target as Node)) {
                setActiveKebabMenu(null);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // 댓글 목록 데이터 가져오기
    const fetchCommentsData = async (page: number, isLoadMore: boolean = false) => {
        if (!diaryId) return;

        try {
            if (isLoadMore) {
                setCommentLoadingMore(true);
            } else {
                setCommentsLoading(true);
            }

            console.log("댓글 목록 조회 시작:", { diaryId, page, isLoadMore });
            const result = await getDiaryComments(diaryId, { page, limit: 20 });
            console.log("댓글 목록 조회 결과:", result);

            if (result && result.commentList) {
                if (isLoadMore) {
                    setComments(prev => [...prev, ...result.commentList]);
                } else {
                    setComments(result.commentList);
                }

                setCommentHasMore(result.pagination.hasNextPage);
                setCommentCurrentPage(page);
                setCommentsPagination(result.pagination);

                console.log("댓글 목록 상태 업데이트 완료:", {
                    commentCount: result.commentList.length,
                    hasMore: result.pagination.hasNextPage
                });
            } else {
                console.log("댓글 목록 결과가 올바르지 않음:", result);
            }
        } catch (error) {
            console.error("댓글 목록 조회 실패:", error);
        } finally {
            if (isLoadMore) {
                setCommentLoadingMore(false);
            } else {
                setCommentsLoading(false);
            }
        }
    };

    // 댓글 목록 새로고침 (댓글 작성/수정/삭제 후 사용)
    const refreshComments = async () => {
        if (!diaryId) return;

        try {
            console.log("댓글 목록 새로고침 시작");
            const result = await getDiaryComments(diaryId, { page: 1, limit: 20 });
            console.log("댓글 목록 새로고침 결과:", result);

            if (result && result.commentList) {
                setComments(result.commentList);
                setCommentHasMore(result.pagination.hasNextPage);
                setCommentCurrentPage(1);
                setCommentsPagination(result.pagination);

                console.log("댓글 목록 새로고침 완료:", {
                    commentCount: result.commentList.length,
                    hasMore: result.pagination.hasNextPage
                });
            }
        } catch (error) {
            console.error("댓글 목록 새로고침 실패:", error);
        }
    };

    // 댓글 제출 핸들러
    const handleCommentSubmit = async () => {
        if (!commentText.trim() || !diaryId) return;

        try {
            setCommentSubmitting(true);
            console.log("댓글 작성 시작:", { diaryId, content: commentText.trim(), parentId: replyToComment });
            const result = await createDiaryComment(diaryId, commentText.trim(), replyToComment || undefined);
            console.log("댓글 작성 결과:", result);

            if (result && result.comment) {
                console.log("✅ 댓글 작성 성공, 목록에 추가:", result.comment.id);

                // 헬퍼 함수를 사용하여 댓글 추가
                setComments(prevComments => 
                    addComment(prevComments, result.comment, replyToComment || undefined)
                );

                // 스크롤 처리
                setTimeout(() => {
                    if (replyToComment) {
                        // 대댓글인 경우: 해당 부모 댓글 위치로 스크롤
                        const commentElement = document.getElementById(`comment-${replyToComment}`);
                        if (commentElement) {
                            commentElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        }
                    } else {
                        // 일반 댓글인 경우: 새 댓글 위치로 스크롤
                        const newCommentElement = document.getElementById(`comment-${result.comment.id}`);
                        if (newCommentElement) {
                            newCommentElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        }
                    }
                }, 100);

                setCommentText("");
                setReplyToComment(null);
                setReplyText("");
            } else {
                console.log("댓글 작성 응답이 올바르지 않음:", result);
            }
        } catch (error) {
            console.error("댓글 작성 실패:", error);
        } finally {
            setCommentSubmitting(false);
        }
    };

    // 댓글 좋아요 토글 (부모/자식 댓글 모두 처리)
    const handleCommentLikeToggle = async (commentId: number) => {
        if (!diaryId) return;

        try {
            setCommentLikeLoading(prev => ({ ...prev, [commentId]: true }));
            console.log("❤️ 댓글 좋아요 토글 시작:", commentId);
            
            const result = await toggleCommentLike(diaryId, commentId);
            console.log("✅ 댓글 좋아요 토글 결과:", { commentId, result });

            // 헬퍼 함수를 사용하여 부모/자식 댓글 모두에서 해당 commentId 찾아서 업데이트
            setComments(prevComments => 
                updateCommentLike(prevComments, commentId, {
                    isLiked: result.isLiked,
                    likeCount: result.likeCount
                })
            );
        } catch (error) {
            console.error("❌ 댓글 좋아요 토글 실패:", error);
        } finally {
            setCommentLikeLoading(prev => ({ ...prev, [commentId]: false }));
        }
    };

    // 댓글 삭제 (부모/자식 댓글 모두 처리)
    const handleCommentDelete = async (commentId: number) => {
        if (!diaryId) return;

        // 헬퍼 함수를 사용하여 부모/자식 댓글에서 해당 commentId 찾기
        const comment = findCommentById(comments, commentId);
        if (!comment) {
            console.error("삭제할 댓글을 찾을 수 없음:", commentId);
            return;
        }

        // 다이어리 작성자이거나 댓글 작성자인 경우에만 삭제 가능
        const isDiaryAuthor = diaryAuthorId === userInfo?.id;
        const isCommentAuthor = comment.userId === userInfo?.id;
        
        if (!isDiaryAuthor && !isCommentAuthor) {
            alert("자신이 작성한 댓글만 삭제할 수 있습니다.");
            return;
        }

        const isConfirmed = window.confirm("정말로 이 댓글을 삭제하시겠습니까?");
        if (isConfirmed) {
            try {
                console.log("🗑️ 댓글 삭제 시작:", commentId);
                await deleteDiaryComment(diaryId, commentId);
                
                // 헬퍼 함수를 사용하여 부모/자식 댓글에서 해당 commentId 제거
                setComments(prevComments => removeCommentById(prevComments, commentId));
                console.log("✅ 댓글 삭제 완료:", commentId);
            } catch (error) {
                console.error("❌ 댓글 삭제 실패:", error);
                alert("댓글 삭제에 실패했습니다. 다시 시도해주세요.");
            }
        }
    };

    // 대댓글 클릭 핸들러
    const handleReplyClick = (commentId: number) => {
        if (replyToComment === commentId) {
            // 이미 대댓글 모드인 경우 취소
            setReplyToComment(null);
            setReplyText("");
        } else {
            // 새로운 대댓글 모드 시작
            setReplyToComment(commentId);
            setReplyText("");
        }
    };

    // 대댓글 취소 핸들러
    const handleCancelReply = () => {
        setReplyToComment(null);
        setReplyText("");
    };

    // 댓글 신고 핸들러
    const handleCommentReport = (commentId: number, commentContent: string) => {
        setCommentToReport({ id: commentId, content: commentContent });
        setShowReportPopup(true);
        setActiveKebabMenu(null);
    };

    // 대댓글 제출 핸들러
    const handleReplySubmit = async () => {
        if (!replyText.trim() || !replyToComment || !diaryId) return;

        try {
            setCommentSubmitting(true);
            const result = await createDiaryComment(diaryId, replyText.trim(), replyToComment);

            if (result && result.comment) {
                // 헬퍼 함수를 사용하여 대댓글 추가
                setComments(prevComments => 
                    addComment(prevComments, result.comment, replyToComment)
                );

                // 해당 부모 댓글 위치로 스크롤
                setTimeout(() => {
                    const commentElement = document.getElementById(`comment-${replyToComment}`);
                    if (commentElement) {
                        commentElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                }, 100);

                // 성공 시 상태 초기화
                setReplyToComment(null);
                setReplyText("");
            }
        } catch (error) {
            console.error("대댓글 작성 실패:", error);
        } finally {
            setCommentSubmitting(false);
        }
    };

    // 무한 스크롤 핸들러
    const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
        const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;

        if (scrollTop + clientHeight >= scrollHeight - 100 && commentHasMore && !commentLoadingMore) {
            fetchCommentsData(commentCurrentPage + 1, true);
        }
    };

    // 내 코멘트인지 확인 (다이어리 작성자인 경우 모든 댓글에 삭제 버튼 표시)
    const isMyComment = (comment: IDiaryComment) => {
        const isDiaryAuthor = diaryAuthorId === userInfo?.id;
        const isCommentAuthor = comment.userId === userInfo?.id;
        return isDiaryAuthor || isCommentAuthor;
    };

    return (
        <div className="space-y-4 p-4">
            {/* 댓글 목록 */}
            <div className="space-y-1.5 mb-20">
                {commentsLoading ? (
                    // 댓글 스켈레톤 UI
                    <>
                        {[1, 2, 3].map((index) => (
                            <div key={`skeleton-${index}`} className="border border-gray-200 rounded-lg p-3 animate-pulse">
                                {/* 프로필 이미지 스켈레톤 */}
                                <div className="flex items-center space-x-2 mb-1.5">
                                    <div className="w-7 h-7 bg-gray-300 rounded-full"></div>
                                    <div className="flex flex-col space-y-1.5">
                                        <div className="h-4 bg-gray-300 rounded w-24"></div>
                                        <div className="h-3 bg-gray-300 rounded w-16"></div>
                                    </div>
                                </div>

                                {/* 댓글 내용 스켈레톤 */}
                                <div className="space-y-2 mb-2">
                                    <div className="h-4 bg-gray-300 rounded w-full"></div>
                                    <div className="h-4 bg-gray-300 rounded w-3/4"></div>
                                </div>

                                {/* 좋아요 버튼 스켈레톤 */}
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-2">
                                        <div className="w-4 h-4 bg-gray-300 rounded"></div>
                                        <div className="h-3 bg-gray-300 rounded w-8"></div>
                                    </div>
                                    <div className="h-3 bg-gray-300 rounded w-16"></div>
                                </div>
                            </div>
                        ))}
                    </>
                ) : comments.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                        <svg className="inline-block w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    </div>
                ) : (
                    comments.map((comment) => (
                        <div key={comment.id} className="space-y-2">
                            {/* 메인 댓글 */}
                            <div id={`comment-${comment.id}`}>
                                <CommentItem
                                    comment={comment}
                                    isChild={false}
                                    onLikeToggle={handleCommentLikeToggle}
                                    onReplyClick={handleReplyClick}
                                    onDeleteClick={handleCommentDelete}
                                    onReportClick={handleCommentReport}
                                    isMyComment={isMyComment}
                                    commentLikeLoading={commentLikeLoading}
                                    showKebabMenu={activeKebabMenu}
                                    onKebabToggle={toggleKebabMenu}
                                    kebabRef={kebabRef}
                                    onUserClick={handleUserClick}
                                />
                            </div>

                            {/* 자식 코멘트들 */}
                            {comment.childList && comment.childList.length > 0 && (
                                <div className="space-y-2">
                                    {comment.childList.map((childComment) => (
                                        <div key={childComment.id} id={`comment-${childComment.id}`}>
                                            <CommentItem
                                                comment={childComment}
                                                isChild={true}
                                                onLikeToggle={handleCommentLikeToggle}
                                                onReplyClick={handleReplyClick}
                                                onDeleteClick={handleCommentDelete}
                                                onReportClick={handleCommentReport}
                                                isMyComment={isMyComment}
                                                commentLikeLoading={commentLikeLoading}
                                                showKebabMenu={activeKebabMenu}
                                                onKebabToggle={toggleKebabMenu}
                                                kebabRef={kebabRef}
                                                onUserClick={handleUserClick}
                                            />
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))
                )}

                {/* 무한 스크롤 로딩 인디케이터 */}
                {commentLoadingMore && (
                    <div className="flex justify-center items-center py-4">
                        <div className="flex items-center space-x-2">
                            <svg className="w-4 h-4 animate-spin text-[#CF3688]" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                        </div>
                    </div>
                )}

                {/* 더 이상 데이터가 없을 때 표시 */}
                {!commentHasMore && comments.length > 0 && (
                    <div className="flex justify-center items-center py-4">
                        <div className="text-center">
                            <div className="w-8 h-8 mx-auto mb-2 bg-gray-100 rounded-full flex items-center justify-center">
                                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* 스티키 코멘트 입력란 */}
            <div className="fixed bottom-4 left-4 right-4 z-10">
                <div className="backdrop-blur-md bg-white/80 rounded-2xl shadow-lg border border-gray-200/50 p-4">
                    {/* 대댓글 표시 영역 */}
                    {replyToComment && (
                        <div className="mb-2 p-2">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    {/* 대댓글 아이콘 */}
                                    <img src="/imgs/icons/icon-reply.svg" alt="대댓글" className="w-4 h-4 text-gray-500" />

                                    {/* 댓글 내용 */}
                                    <span className="text-sm text-gray-600 truncate max-w-[200px]">
                                        {comments.find(c => c.id === replyToComment)?.contents || ""}
                                    </span>
                                </div>

                                {/* 삭제 버튼 */}
                                <button
                                    onClick={handleCancelReply}
                                    className="text-gray-400 hover:text-gray-600 transition-colors"
                                >
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    )}

                    {/* 댓글 입력 폼 */}
                    <div className="flex gap-2">
                        <input
                            type="text"
                            value={replyToComment ? replyText : commentText}
                            onChange={(e) => replyToComment ? setReplyText(e.target.value) : setCommentText(e.target.value)}
                            placeholder={replyToComment
                                ? "대댓글을 입력하세요..."
                                : (getLanguageValue(PAGE_TYPE.STUDENT, "growthDiary.diary.txtCommentPlaceholder") || "댓글을 입력하세요...")
                            }
                            maxLength={200}
                            disabled={commentSubmitting}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#CF3688] focus:border-[#CF3688] text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                            onKeyPress={(e) => e.key === 'Enter' && !commentSubmitting && (replyToComment ? handleReplySubmit() : handleCommentSubmit())}
                        />
                        <button
                            onClick={replyToComment ? handleReplySubmit : handleCommentSubmit}
                            disabled={replyToComment ? !replyText.trim() : !commentText.trim() || commentSubmitting}
                            className="px-4 py-2 bg-[#CF3688] text-white rounded-lg hover:bg-[#CF3688]/90 focus:outline-none focus:ring-2 focus:ring-[#CF3688] disabled:cursor-not-allowed transition-colors text-sm font-medium"
                        >
                            {replyToComment ? "답글" : (getLanguageValue(PAGE_TYPE.STUDENT, "growthDiary.diary.btnCommentSubmit") || "입력")}
                        </button>
                    </div>
                </div>
            </div>

            {/* 댓글 신고 팝업 */}
            {showReportPopup && commentToReport && (
                <ReportPopup
                    type="comment"
                    diaryId={diaryId}
                    commentId={commentToReport.id.toString()}
                    content={commentToReport.content}
                    onClose={() => {
                        setShowReportPopup(false);
                        setCommentToReport(null);
                    }}
                    onSuccess={() => {
                        // 신고 성공 시 처리 (필요시 추가)
                        console.log('댓글 신고가 완료되었습니다.');
                    }}
                />
            )}
        </div>
    );
};

export default CommentSection;
